import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";

import { Chess, type Square } from "chess.js";

import { lichessBoardPalette } from "@/shared/config/siteContent";
import { moveFromUci, replayUciMove, resolveUciMove } from "@/shared/ui/chessMove";
import { ChessSurface } from "@/shared/ui/ChessSurface";

import { buildLivestreamDisplayBoard } from "./livestreamPosition";
import type {
  LivestreamGuessResult,
  LivestreamSnapshot,
  SelectedLivestreamMove,
} from "./types";

type LivestreamBoardProps = {
  snapshot: LivestreamSnapshot;
  displayPly: number;
  interactive: boolean;
  selectedMove: SelectedLivestreamMove | null;
  result: LivestreamGuessResult | null;
  onMoveSelected: (move: SelectedLivestreamMove | null) => void;
};

export function LivestreamBoard({
  snapshot,
  displayPly,
  interactive,
  selectedMove,
  result,
  onMoveSelected,
}: LivestreamBoardProps) {
  const boardWrapRef = useRef<HTMLDivElement | null>(null);
  const [boardWidth, setBoardWidth] = useState(0);
  const [selectedSquare, setSelectedSquare] = useState<Square | null>(null);
  const [legalTargets, setLegalTargets] = useState<Square[]>([]);
  const game = useMemo(
    () => buildLivestreamDisplayBoard(snapshot, displayPly),
    [displayPly, snapshot],
  );

  useEffect(() => {
    const node = boardWrapRef.current;
    if (!node) {
      return;
    }

    const updateWidth = () => {
      const next = Math.floor(Math.min(Math.max(node.clientWidth - 28, 238), 560));
      setBoardWidth(next > 0 ? next : 320);
    };

    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setSelectedSquare(null);
    setLegalTargets([]);
    onMoveSelected(null);
  }, [displayPly, snapshot.position_id]);

  useEffect(() => {
    if (interactive || selectedMove) {
      return;
    }
    setSelectedSquare(null);
    setLegalTargets([]);
  }, [interactive, selectedMove]);

  const lastMove = useMemo(() => {
    const history = game.history({ verbose: true });
    return history.at(-1) ?? null;
  }, [game]);

  const selectSquare = (square: Square) => {
    if (!interactive) {
      return;
    }
    const moves = game.moves({ square, verbose: true });
    if (moves.length === 0) {
      setSelectedSquare(null);
      setLegalTargets([]);
      onMoveSelected(null);
      return;
    }
    setSelectedSquare(square);
    setLegalTargets(moves.map((move) => move.to));
  };

  const resolveSelection = (from: Square, to: Square) => {
    const board = new Chess(game.fen());
    const moveUci = resolveUciMove(board, from, to);
    if (!moveUci) {
      return false;
    }
    const move = board.move(moveFromUci(moveUci));
    if (!move) {
      return false;
    }
    onMoveSelected({
      uci: moveUci,
      from: move.from,
      to: move.to,
      san: move.san,
    });
    setSelectedSquare(from);
    setLegalTargets([to]);
    return true;
  };

  const onSquareClick = (squareName: Square) => {
    if (!interactive) {
      return;
    }

    if (selectedSquare) {
      if (squareName === selectedSquare) {
        setSelectedSquare(null);
        setLegalTargets([]);
        onMoveSelected(null);
        return;
      }
      if (resolveSelection(selectedSquare, squareName)) {
        return;
      }
    }

    const piece = game.get(squareName);
    if (!piece || piece.color !== game.turn()) {
      setSelectedSquare(null);
      setLegalTargets([]);
      onMoveSelected(null);
      return;
    }
    selectSquare(squareName);
  };

  const onPieceDrop = (sourceSquare: Square, targetSquare?: Square) => {
    if (!interactive || !targetSquare) {
      return false;
    }
    return resolveSelection(sourceSquare, targetSquare);
  };

  const customSquareStyles = useMemo(() => {
    const styles: Record<string, CSSProperties> = {};

    if (lastMove) {
      styles[lastMove.from] = { backgroundColor: lichessBoardPalette.lastMove };
      styles[lastMove.to] = { backgroundColor: lichessBoardPalette.lastMove };
    }

    if (selectedSquare) {
      styles[selectedSquare] = {
        ...(styles[selectedSquare] ?? {}),
        boxShadow: lichessBoardPalette.selected,
      };
    }

    for (const square of legalTargets) {
      styles[square] = {
        ...(styles[square] ?? {}),
        background: lichessBoardPalette.legalTarget,
      };
    }

    if (selectedMove) {
      styles[selectedMove.from] = {
        ...(styles[selectedMove.from] ?? {}),
        boxShadow: "inset 0 0 0 5px rgba(255, 116, 71, 0.96)",
      };
      styles[selectedMove.to] = {
        ...(styles[selectedMove.to] ?? {}),
        backgroundColor: "rgba(255, 116, 71, 0.26)",
      };
    }

    if (result && !result.is_correct) {
      const target = moveFromUci(result.target_move);
      styles[target.to] = {
        ...(styles[target.to] ?? {}),
        boxShadow: "inset 0 0 0 5px rgba(255, 75, 99, 0.96)",
      };
    }

    if (result?.is_correct) {
      const target = moveFromUci(result.target_move);
      styles[target.to] = {
        ...(styles[target.to] ?? {}),
        boxShadow: "inset 0 0 0 5px rgba(183, 255, 74, 0.98)",
      };
    }

    return styles;
  }, [lastMove, legalTargets, result, selectedMove, selectedSquare]);

  return (
    <div className="livestream-board" ref={boardWrapRef}>
      {boardWidth > 0 ? (
        <ChessSurface
          id="homepage-livestream-board"
          boardWidth={boardWidth}
          position={game.fen()}
          arePiecesDraggable={interactive}
          onPieceDrop={onPieceDrop}
          onPieceDragBegin={(_piece, sourceSquare) => selectSquare(sourceSquare)}
          onSquareClick={onSquareClick}
          customSquareStyles={customSquareStyles}
          darkSquareColor={lichessBoardPalette.dark}
          lightSquareColor={lichessBoardPalette.light}
        />
      ) : null}
    </div>
  );
}
